<div
  class="style2023"
  [ngClass]="{ card: !isInDialog }"
  [ngStyle]="{ padding: isInDialog ? '10px' : '15px' }"
>
  <div class="travel-preference-header">
    <h1 *ngIf="!isInDialog">Juno Settings</h1>
    <div
      *ngIf="dailyChangeLimitDetails?.blockAdditionalChanges"
      class="travel-pref-limit-reached-alert-container"
    >
      <div class="flex grid-gap-10">
        <div class="flex-shrink-1 travel-pref-limit-reached-alert-icon">
          <i class="fa fa-info-circle" style="color: #012169"></i>
        </div>
        <div class="flex-grow-1">
          You've reached your limit for preference changes today. This helps keep Juno running
          smoothly. You can update your preferences again
          <strong
            >tomorrow, {{ dailyChangeLimitDetails?.resetTime | date : 'EEEE, d MMMM' }}</strong
          >
        </div>
      </div>
    </div>
    <div
      *ngIf="isInDialog && !dailyChangeLimitDetails?.blockAdditionalChanges"
      class="planned-route-additional-info"
    >
      Customise your Juno preferences for a personalised route
    </div>
    <div class="pref-count-pill" *ngIf="dailyChangeLimitDetails">
      <i
        class="fa fa-info-circle pref-count-pill-i"
        aria-hidden="true"
        [pTooltip]="
          'To keep Juno running smoothly, changes to preferences are limited to ' +
          dailyChangeLimitDetails?.dailyChangeLimit +
          ' per day.'
        "
        tooltipPosition="bottom"
      ></i>
      {{ dailyChangeLimitDetails.currentChangeCount }}/{{
        dailyChangeLimitDetails.dailyChangeLimit
      }}
      Changes Used
    </div>
  </div>
  <form [formGroup]="prefsForm">
    <div class="input-row">
      <div class="col1">
        <div class="input-heading">Start Time</div>
        <div class="input-sub-heading">
          Choose the earliest time you are willing to leave the house.
        </div>
      </div>
      <div class="col2">
        <div class="flex">
          <div class="input-group">
            <div class="input-group-item">
              <p-dropdown
                class="input-group-filter no-min-width"
                formControlName="startTimeHour"
                [options]="hourOptions"
                [style]="{ width: '65px' }"
                [disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
              ></p-dropdown>
            </div>
          </div>
          <div style="line-height: 38px; display: inline-block; padding: 0 5px">:</div>
          <div class="input-group">
            <div class="input-group-item">
              <p-dropdown
                class="input-group-filter no-min-width"
                formControlName="startTimeMinute"
                [options]="minuteOptions"
                [style]="{ width: '65px' }"
                [disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
              ></p-dropdown>
            </div>
          </div>
          <div style="padding: 10px 0 0 10px">AM</div>
        </div>
      </div>
    </div>

    <div class="input-row">
      <div class="col1">
        <div class="input-heading">
          Public Transport Contingency
          <i
            class="fa fa-info-circle"
            aria-hidden="true"
            pTooltip="Setting this to a larger value gives you more time per connection, but Juno may fit fewer tasks into a day.<br><br>Setting this to a smaller value increases the risk of being late — it's always your responsibility to arrive on time."
            [escape]="false"
          ></i>
        </div>
        <div class="input-sub-heading">
          Extra time added to each full public transport connection to account for waiting for the
          first service, and delays. This does not affect the estimated connection times you see,
          but will affect itinerary windows.
        </div>
      </div>
      <div class="col2">
        <div class="input-group" style="max-width: 300px">
          <div class="input-group-item">
            <p-dropdown
              class="input-group-filter"
              formControlName="publicTransportContingency"
              [options]="contingencyOptions"
              [disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
            ></p-dropdown>
          </div>
        </div>
      </div>
    </div>

    <div class="input-row">
      <div class="col1">
        <div class="input-heading">
          Bicycle Information
          <i
            class="fa fa-info-circle"
            aria-hidden="true"
            pTooltip="Please check yourself that bikes are permitted on all the services you plan to use."
            [escape]="false"
          ></i>
        </div>
        <div class="input-sub-heading">Do you have access to a foldable bike?</div>
      </div>
      <div class="col2">
        <div>
          <p-checkbox
            class="style2023"
            label="Yes, I have access to a foldable bike"
            formControlName="hasBike"
            id="hasBike"
            name="hasBike"
            binary="true"
            [disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
          ></p-checkbox>
        </div>
        <div>
          <div class="bicycle-line-1">
            Select the max. distance you are willing to cycle in a day.
          </div>
          <div class="line-2">
            <div class="input-group" style="max-width: 300px">
              <div class="input-group-item">
                <p-dropdown
                  class="input-group-filter"
                  formControlName="bikeMiles"
                  [options]="cycleDistanceOptions"
                  [disabled]="
                    !prefsForm?.get('hasBike')?.value ||
                    dailyChangeLimitDetails?.blockAdditionalChanges
                  "
                ></p-dropdown>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="input-row">
      <div class="col1">
        <div class="input-heading">
          Time/Cost Priority
          <i
            class="fa fa-info-circle"
            aria-hidden="true"
            pTooltip="Experiment with this slider based on how Juno’s public transport suggestions meet your preferred modes of transport.<br><br>Towards cheaper: Juno will favour e.g. buses and walking. At the cheapest setting, routes that need a taxi to meet time limits may not appear.<br><br>Towards faster: Juno will favour e.g. taxis. At the fastest setting, routes may not appear because they fall below the earnings threshold."
            [escape]="false"
          ></i>
        </div>
        <div class="input-sub-heading">
          Select your time vs cost preference for public transport connections (e.g. taxi vs bus).
        </div>
      </div>
      <div class="col2">
        <div
          class="slider-with-labels"
          [class.disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
        >
          <p-slider
            formControlName="timeCostPriority"
            [min]="0"
            [max]="1"
            [step]="0.125"
            [range]="false"
            [disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
          ></p-slider>

          <div class="slider-ticks" aria-hidden="true">
            <span
              class="tick"
              *ngFor="let t of sliderTicks; let i = index"
              (click)="prefsForm?.get('timeCostPriority')?.setValue(i / 8)"
              [ngClass]="{ edge: i === 0 || i === sliderTicks.length - 1 }"
            ></span>
          </div>

          <div class="slider-labels">
            <div
              class="slider-label at-start"
              (click)="prefsForm?.get('timeCostPriority')?.setValue(0)"
              [class.disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
            >
              <span class="label-text">Cheapest</span>
            </div>
            <div
              class="slider-label at-middle"
              (click)="prefsForm?.get('timeCostPriority')?.setValue(0.5)"
              [class.disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
            >
              <span class="label-text">Balanced</span>
            </div>
            <div
              class="slider-label at-end"
              (click)="prefsForm?.get('timeCostPriority')?.setValue(1)"
              [class.disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
            >
              <span class="label-text">Fastest</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="input-row no-sidebar" *ngIf="showExtremeWarning">
      <div class="col2">
        <div
          class="extreme-warning card"
          [ngClass]="{ cheapest: isExtremeCheapest, fastest: isExtremeFastest }"
        >
          <div
            class="warning-icon"
            [ngClass]="{ cheapest: isExtremeCheapest, fastest: isExtremeFastest }"
          >
            <i
              class="fa"
              [ngClass]="{ 'fa-bus': isExtremeCheapest, 'fa-taxi': isExtremeFastest }"
              aria-hidden="true"
            ></i>
          </div>
          <div class="warning-content">
            <div class="warning-title">{{ extremeWarningTitle }}</div>
            <div class="warning-body">
              {{ extremeWarningBody }}
            </div>
            <div class="warning-tip">
              <i class="fa fa-lightbulb-o" aria-hidden="true"></i>
              Most people find a balance in the middle works best.
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="input-row">
      <div class="col1">
        <div class="input-heading">
          Preferred Train Stations
          <i
            class="fa fa-info-circle"
            aria-hidden="true"
            pTooltip="Nearest isn’t always best — choose stations on different lines to widen your options.<br><br>Driving and need your car back? Select <b>'Routes must start and end at this station'</b>."
            [escape]="false"
          ></i>
        </div>
        <div class="input-sub-heading">
          Choose up to 3 preferred train stations near your home for Juno to use when planning
          routes.
        </div>
      </div>

      <div class="col2">
        <div class="flex">
          <div class="input-group" style="width: 300px">
            <div class="input-group-item" style="position: relative">
              <input
                pInputText
                type="text"
                placeholder="Search for train stations"
                formControlName="station"
                style="padding-left: 20px"
                (focusin)="focusStart()"
                [disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
              />
              <div
                class="la la-search inline-block la-flip-horizontal"
                style="
                  position: absolute;
                  left: 10px;
                  line-height: 35px;
                  font-size: 16px;
                  color: #aaa;
                "
              ></div>
            </div>
          </div>
        </div>

        <div
          *ngIf="
            showSuggestions &&
            preferredStations?.length < 3 &&
            !dailyChangeLimitDetails?.blockAdditionalChanges
          "
          class="card suggestions-card"
        >
          <div
            class="flex"
            style="padding: 5px 10px; background-color: #eee; border-radius: 7px 7px 0 0"
          >
            <div class="flex-grow-1"><b>Suggestions</b></div>
            <div (click)="showSuggestions = false"><i class="fa fa-times"></i></div>
          </div>
          <div class="suggestions-scroll">
            <table class="recommended-table" style="border-radius: 8px; table-layout: fixed">
              <tr
                *ngFor="
                  let station of filteredSuggestions(f.station, suggestions, preferredStations)
                "
                (click)="addToList(station)"
              >
                <td class="icon-cell text-center" style="max-width: 100px">
                  <img src="/assets/images/icons/train.png" alt="Train station" />
                </td>
                <td>
                  <span class="station-name">{{ station.stationName }}</span>
                </td>
                <td class="station-distance">{{ station.distance | number : '1.1-1' }} miles</td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>
  </form>
  <form>
    <div class="input-row no-sidebar">
      <div class="col1"></div>
      <div class="col2 flex">
        <div class="station-list pt-2 flex-grow-1">
          <p-table
            [value]="preferredStations"
            [style]="{ width: '100%' }"
            [tableStyle]="{ 'table-layout': 'auto', width: '100%' }"
          >
            <ng-template pTemplate="header">
              <tr class="preferred-header">
                <th style="text-align: left">Station</th>
                <th>Routes must start and end at this station</th>
                <th *ngIf="!dailyChangeLimitDetails?.blockAdditionalChanges">Remove</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="3" style="text-align: center; padding: 20px">
                  <span *ngIf="!loadingSupplierStations">No preferred stations selected.</span>
                  <span *ngIf="loadingSupplierStations"><i class="fa fa-spin fa-spinner"></i></span>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-data>
              <ng-container *ngIf="typedSupplierTravelStation(data) as args">
                <tr>
                  <td class="icon-cell">
                    <img src="/assets/images/icons/train.png" alt="Train station" />
                    {{ args?.stationName }}
                  </td>
                  <td class="text-center">
                    <input
                      class="station-checkbox"
                      type="checkbox"
                      (click)="toggleStation(args.id, 'toStationDrive')"
                      [checked]="stationStatus(args.id, 'toStationDrive')"
                      [disabled]="dailyChangeLimitDetails?.blockAdditionalChanges"
                    />
                  </td>
                  <td
                    class="text-center cursor-pointer trash-icon"
                    *ngIf="!dailyChangeLimitDetails?.blockAdditionalChanges"
                  >
                    <i class="fa fa-trash" (click)="removeFromList(args)"></i>
                  </td>
                </tr>
              </ng-container>
            </ng-template>
          </p-table>
        </div>
      </div>
    </div>
    <div class="mt-2" *ngIf="!isInDialog">
      <div class="flex">
        <div class="flex-grow-1">
          <button
            pButton
            label="Take me to Route Search"
            class="btn-primary"
            (click)="toPlannedRoutesPage()"
          ></button>
        </div>
        <div class="flex-shrink-1">
          <button
            pButton
            class="btn-primary"
            (click)="saveChanges()"
            style="line-height: 31px"
            [disabled]="saving || dailyChangeLimitDetails?.blockAdditionalChanges"
          >
            <i class="fa fa-spin fa-spinner" *ngIf="saving"></i> Save
          </button>
        </div>
      </div>
    </div>
  </form>
</div>
